/**
 * NeuralSearch Component
 * Semantic search interface for Lemma's brain
 */

import React, { useState } from 'react';
import { Search, Brain, Clock, Copy, ChevronRight, Sparkles, Database } from 'lucide-react';
import { useDashboardStore } from '../store/dashboardStore';
import { useAPI } from '../hooks/useAPI';
import toast from 'react-hot-toast';

export const NeuralSearch: React.FC = () => {
  const [query, setQuery] = useState('');
  const { fetchSearch } = useAPI();
  const results = useDashboardStore((state) => state.searchResults);
  const isSearching = useDashboardStore((state) => state.isSearching);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      fetchSearch(query);
    }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Search Header */}
      <div className="px-6 py-8 flex flex-col items-center justify-center text-center space-y-4 flex-shrink-0">
        <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-2xl flex items-center justify-center shadow-[0_0_30px_rgba(99,102,241,0.4)] mb-2 animate-pulse">
          <Brain className="w-10 h-10 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-black text-white uppercase tracking-tighter">The Brain</h1>
          <p className="text-slate-500 text-sm font-medium mt-1">Neural Semantic Search through your entire AI history</p>
        </div>

        <form onSubmit={handleSearch} className="w-full max-w-2xl relative mt-4">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search for a concept, code snippet, or past solution..."
            className="w-full bg-slate-900/60 border border-white/10 rounded-2xl px-6 py-4 pl-14 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all shadow-2xl backdrop-blur-xl"
          />
          <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-6 h-6 text-slate-500" />
          <button
            type="submit"
            disabled={isSearching}
            className="absolute right-3 top-1/2 -translate-y-1/2 bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 rounded-xl font-bold text-sm transition-colors disabled:opacity-50"
          >
            {isSearching ? 'Thinking...' : 'Search'}
          </button>
        </form>
      </div>

      {/* Results Area */}
      <div className="flex-1 overflow-y-auto px-6 pb-8">
        {results.length === 0 && !isSearching ? (
          <div className="flex flex-col items-center justify-center h-full text-center space-y-4 opacity-50">
            <Database className="w-12 h-12 text-slate-700" />
            <div className="max-w-xs">
              <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">No memories found</p>
              <p className="text-slate-600 text-xs mt-2">Try searching for something you've asked your AI before, like "sorting algorithms" or "Stripe integration".</p>
            </div>
          </div>
        ) : (
          <div className="max-w-4xl mx-auto space-y-4">
            {results.map((result) => (
              <div
                key={result.id}
                className="bg-slate-900/40 border border-white/5 rounded-2xl overflow-hidden hover:border-white/10 transition-all group"
              >
                <div className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-white/5 rounded-lg flex items-center justify-center border border-white/5">
                        <Sparkles className="w-4 h-4 text-indigo-400" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Semantic Match</span>
                        <span className="text-[10px] font-bold text-indigo-400">{(result.similarity * 100).toFixed(1)}% match score</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1.5 text-slate-500">
                        <Clock className="w-3.5 h-3.5" />
                        <span className="text-[10px] font-bold uppercase">{formatTime(result.timestamp)}</span>
                      </div>
                      <button
                        onClick={() => handleCopy(result.prompt + '\n\n' + (typeof result.response === 'string' ? result.response : JSON.stringify(result.response)))}
                        className="p-2 hover:bg-white/5 rounded-lg text-slate-500 hover:text-white transition-colors"
                        title="Copy full interaction"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <h4 className="text-[10px] font-black text-slate-600 uppercase tracking-widest mb-2">Prompt</h4>
                      <div className="bg-black/20 rounded-xl p-4 text-sm text-slate-300 font-medium border border-white/5 line-clamp-3 group-hover:line-clamp-none transition-all">
                        {result.prompt}
                      </div>
                    </div>
                    <div>
                      <h4 className="text-[10px] font-black text-slate-600 uppercase tracking-widest mb-2">Response</h4>
                      <div className="bg-indigo-500/5 rounded-xl p-4 text-sm text-slate-200 border border-indigo-500/10 font-mono whitespace-pre-wrap">
                        {typeof result.response === 'string' 
                          ? result.response 
                          : result.response?.content?.[0]?.text || JSON.stringify(result.response, null, 2)}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-white/5 flex justify-end">
                    <button
                      onClick={() => {
                        const el = document.getElementById(`thread-${result.id}`);
                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }}
                      className="flex items-center gap-1 text-[10px] font-black text-indigo-400 uppercase tracking-widest hover:text-indigo-300 transition-colors"
                    >
                      View Full Thread
                      <ChevronRight className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
